<template>
  <v-row class="calendar-page mx-0">
    <MomentumScrolling :content-classes="{'paper-background': true, 'pt-16': true}">
      <Scribbles :num="8" />
      <v-col cols="auto" class="live-collage mx-auto grow">
        <v-responsive :aspect-ratio="$vuetify.breakpoint.mdAndUp ? 1300/910 : null" class="mx-auto py-16 py-md-0" style="overflow: visible;">
          <div class="collage-content py-8 px-6 px-md-16 paper-background taped taped--rotated">
            <h1 class="display-4 mb-2">{{$root.siteSettings.liveTitle}}</h1>
            <h2 class="subtitle display-1 my-2">{{new Date($root.siteSettings.liveStartDate).toLocaleDateString($language.current, {day: '2-digit'})}}-{{new Date($root.siteSettings.liveEndDate).toLocaleDateString($language.current, {day: '2-digit'})}} {{new Date($root.siteSettings.liveEndDate).toLocaleDateString($language.current, {month: 'long', year: 'numeric'})}}</h2>
            <MarkdownBlock :text="$vuetify.breakpoint.smAndUp ? $root.siteSettings.liveDescription : $root.siteSettings.liveDescriptionShort" class="body-1 my-4" />
            <a v-if="$root.siteSettings.liveNow" :href="getCurrentlyLiveEvent() ? getCurrentlyLiveEvent().liveLink.url : null" rel="noopener" target="_blank" class="live-link display-2 red--text" :class="{disabled: !getCurrentlyLiveEvent()}"><Arrow class="arrow" />{{getCurrentlyLiveEvent() ? getCurrentlyLiveEvent().liveLink.label : $root.siteSettings.liveCTA.text}}</a>
            <div v-else class="watch-the-talks display-2 red--text"><Arrow class="arrow" />Watch the talks</div>
          </div>
        </v-responsive>
      </v-col>
      <!-- live now -->
      <template v-if="$root.siteSettings.liveNow">
        <v-col cols="12" offset="0" lg="8" offset-lg="2" class="px-8 pt-16">
          <h1 class="display-2">Program</h1>
        </v-col>
        <v-col cols="12" offset="0" lg="8" offset-lg="2" class="px-8">
          <v-row class="calendar-container" style="position: relative;">
            <DeletedText1 v-if="$vuetify.breakpoint.mdAndUp" class="deleted-text deleted-text--1" />
            <DeletedText2 v-if="$vuetify.breakpoint.mdAndUp" class="deleted-text deleted-text--2" />
            <DeletedText3 v-if="$vuetify.breakpoint.mdAndUp" class="deleted-text deleted-text--3" />
            <v-col cols="12" md="2" class="pt-md-16">
              <h2 class="text-md-center d-block display-3 pl-md-2 mc-4 mb-md-12">Dates</h2>
              <v-btn text block tile class="date-item my-2 pl-0" color="primary" :to="{params: {date: date}}" v-for="date in dates" :key="date">
                <span class="body-1 highlighted-item highlighted-item--center highlighted-item--red">
                  {{new Date(`${date}T12:00:00`).toLocaleDateString($language.current, { month: 'long', day: 'numeric' })}}
                </span>
              </v-btn>
            </v-col>
            <v-col cols="12" md="10">
              <v-row style="min-height: 50vh;" align="center" justify="center" v-if="!allCalendarEvents || $apolloData.queries.LiveEvents.loading">
                <v-col cols="auto">
                  <div class="animated-eye" />
                </v-col>
              </v-row>
              <v-row align="center" justify="center" no-gutters v-else class="program-holder">
                <v-col cols="0" md="2" />
                <v-col cols="auto" class="grow" >
                  <v-calendar
                    event-color="transparent"
                    :value="this.date"
                    type="day"
                    :locale="$language.current"
                    event-start="startingAt"
                    event-end="endingAt"
                    event-name="title"
                    hide-header
                    :event-ripple="false"
                    :interval-minutes="30"
                    :first-interval="firstEventInterval"
                    :interval-count="lastEventInterval - firstEventInterval"
                    :interval-height="$vuetify.breakpoint.smAndDown ? 140 : 80"
                    :events="allCalendarEvents"
                  >
                    <!-- interval-width="400" -->
                    <template v-slot:event="{event}">
                      <v-row class="fill-height pa-4 black--text program-event highlighted-item highlighted-item--top body-2 pl-md-12" data-cursor-target="arrow"  align="start" no-gutters>
                        <v-col :style="{overflow: 'hidden', maxHeight: '100%'}">
                          <span class="event-btn">{{event.title}}</span><br />
                          <v-btn v-if="event.moreInfoLink" text small class="body-2 text-decoration-underline event-btn my-2 pl-0" :href="event.moreInfoLink.url" target="_blank" rel="noopener">{{event.moreInfoLink.label}}</v-btn>
                          <v-btn v-if="isCurrentlyLive(event)"  text small class="text-script event-btn my-2" :href="event.liveLink.url" target="_blank" rel="noopener">{{event.liveLink.label}}</v-btn>
                          <v-btn v-else-if="event.recordingURL && (event.recordingURL.toLowerCase().indexOf('youtu') !== -1 || event.recordingURL.toLowerCase().indexOf('vimeo') !== -1 || event.recordingURL.toLowerCase().indexOf('cove.') !== -1)" text small class="text-script event-btn my-2" :to="{name: 'LiveEventPlayer', params: {slug: event._slug}}">Watch the video</v-btn>
                          <v-btn v-else-if="event.recordingURL" text small class="text-script event-btn my-2" :href="event.recordingURL" target="_blank" rel="noopener">Watch the video</v-btn>
                        </v-col>
                      </v-row>
                    </template>
                  </v-calendar>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </template>
      <!-- post live mode -->
      <template v-else>
        <v-col cols="12" offset="0" lg="8" offset-lg="2" class="px-8 pt-16 mt-16">
          <h1 class="display-2">Past Talks</h1>
        </v-col>
        <v-col cols="12" offset="0" md="9" offset-md="3" class="w-100 pt-16 mt-8 pt-md-0 mt-md-0" v-for="(day, dayIndex) in days" :key="dayIndex">
          <h2 class="title">{{new Date(day.date).toLocaleDateString($language.current, {weekday: 'long', day: 'numeric', year: 'numeric', month: 'long'})}}</h2>
          <CardGallery
            v-if="LiveEvents"
            :data="day.events"
            :cols="$vuetify.breakpoint.mdAndUp ? 5 : 10" />
        </v-col>
      </template>
    </MomentumScrolling>
  </v-row>
</template>
<script>
import find from 'lodash/find'
import findLast from 'lodash/findLast'
import uniq from 'lodash/uniq'
import sortBy from 'lodash/sortBy'
import reject from 'lodash/reject'

import ListLiveEvent from '@/graphql/ListLiveEvent.gql'
import Scribbles from '@/molecules/Scribbles'
import MomentumScrolling from '@/atoms/MomentumScrolling'
import { MarkdownBlock } from '@monogrid/vue-lib'
import Arrow from '@/assets/img/cursors/arrow.svg'
import DeletedText1 from '@/assets/img/deleted-text-1.svg'
import DeletedText2 from '@/assets/img/deleted-text-2.svg'
import DeletedText3 from '@/assets/img/deleted-text-3.svg'
import CardGallery from '@/molecules/CardGallery'

export default {
  name: 'ProgramPage',
  props: {
    date: {
      required: false,
      type: String
    }
  },
  components: {
    Scribbles,
    MomentumScrolling,
    MarkdownBlock,
    Arrow,
    DeletedText1,
    DeletedText2,
    DeletedText3,
    CardGallery
  },
  apollo: {
    LiveEvents: {
      query: ListLiveEvent,
      // skip () {
      //   return !this.date
      // },
      variables () {
        return {
          status: 'PUBLISHED',
          sortOn: 'startingAt',
          descending: false
          // filter: { field: 'startingAt', operator: 'beginsWith', operand: this.date.split('T')[0] }
        }
      }
    }
  },
  mounted () {
    this.selectFirstDate()
  },
  methods: {
    getCurrentlyLiveEvent () {
      if (!this.allCalendarEvents) return null
      for (let i = 0; i < this.allCalendarEvents.length; i++) {
        if (this.isCurrentlyLive(this.allCalendarEvents[i])) {
          return this.allCalendarEvents[i]
        }
      }
      return null
    },
    isCurrentlyLive (event) {
      const now = this.$root.getNow().getTime()
      const start = new Date(event.startingAt).getTime()
      const end = new Date(event.endingAt).getTime()
      return now >= start && now < end
    },
    selectFirstDate () {
      if (!this.date && this.dates) {
        this.$router.replace({ params: { date: this.dates[0] } })
      }
    },
    /**
     * from ISO string to loca
     */
    getYYYYMMDD (d0) {
      const d = new Date(d0)
      return new Date(d.getTime() - d.getTimezoneOffset() * 60 * 1000).toISOString().split('T')[0]
    }
  },

  watch: {
    date (value) {
      this.selectFirstDate()
    },
    dates (value) {
      this.selectFirstDate()
    }
  },

  computed: {
    days () {
      if (this.LiveEvents) {
        const validEvents = reject(this.LiveEvents.items, { recordingURL: null })
        const days = {}
        validEvents.forEach(event => {
          const day = event.startingAt.split('T')[0]
          if (!days[day]) days[day] = []
          days[day].push(event)
        })
        const retValue = []

        Object.keys(days).forEach(day => retValue.push({ date: day, events: days[day] }))

        return retValue
      }
      return []
    },
    allCalendarEvents () {
      if (this.LiveEvents) {
        this.$root.$emit('scroll-to-saved-position')

        return sortBy(this.LiveEvents.items.map(event => {
          return {
            ...event,
            timed: true,
            startingAt: new Date(event.startingAt),
            endingAt: new Date(event.endingAt)
          }
        }), evt => evt.startingAt.getTime())
      }
      return null
    },
    dates () {
      if (this.allCalendarEvents) {
        const dates = []
        this.allCalendarEvents.forEach(evt => {
          // dates.push(this.getYYYYMMDD(evt.startingAt.toISOString()))
          // dates.push(this.getYYYYMMDD(evt.startingAt.toISOString()))
          // dates.push(evt.startingAt.toISOString().split('T')[0])
          dates.push(`${evt.startingAt.getFullYear()}-${(evt.startingAt.getMonth() + 1).toString().padStart(2, '0')}-${evt.startingAt.getDate().toString().padStart(2, '0')}`)
        })

        return uniq(dates)
      }
      return []
    },
    firstEvent () {
      return find(this.allCalendarEvents, evt => {
        const startsAtDate = evt.startingAt.getMonth() === new Date(`${this.date}T12:00:00`).getMonth() && evt.startingAt.getDate() === new Date(`${this.date}T12:00:00`).getDate()
        const endsAtDate = evt.endingAt.getMonth() === new Date(`${this.date}T12:00:00`).getMonth() && evt.endingAt.getDate() === new Date(`${this.date}T12:00:00`).getDate()
        return startsAtDate || endsAtDate
      })
    },
    lastEvent () {
      return findLast(this.allCalendarEvents, evt => {
        const startsAtDate = evt.startingAt.getMonth() === new Date(`${this.date}T12:00:00`).getMonth() && evt.startingAt.getDate() === new Date(`${this.date}T12:00:00`).getDate()
        const endsAtDate = evt.endingAt.getMonth() === new Date(`${this.date}T12:00:00`).getMonth() && evt.endingAt.getDate() === new Date(`${this.date}T12:00:00`).getDate()
        return startsAtDate || endsAtDate
      })
    },
    firstEventInterval () {
      if (this.allCalendarEvents) {
        let hours
        let minutes
        if (!this.firstEvent || this.firstEvent.endingAt.getDate() !== this.firstEvent.startingAt.getDate()) {
          hours = 0
          minutes = 0
        } else {
          hours = this.firstEvent.startingAt.getHours()
          minutes = this.firstEvent.startingAt.getMinutes()
          console.log('first', this.firstEvent.startingAt, this.firstEvent.endingAt, hours, minutes)
        }

        // console.log('first', hours, minutes)

        return Math.max(0, (hours * 2) + Math.floor(minutes / 30) - 2)
      }
      return 0
    },
    lastEventInterval () {
      if (this.allCalendarEvents) {
        if (this.allCalendarEvents) {
          let hours
          let minutes
          if (!this.lastEvent || this.lastEvent.endingAt.getDate() !== this.lastEvent.startingAt.getDate()) {
            hours = 24
            minutes = 59
          } else {
            hours = this.lastEvent.endingAt.getHours()
            minutes = this.lastEvent.endingAt.getMinutes()
            console.log('last', this.lastEvent.startingAt, this.lastEvent.endingAt, hours, minutes)
          }
          // console.log('last', hours, minutes)

          return Math.max(this.firstEventInterval + 2, Math.min(50, (hours * 2) + Math.floor(minutes / 30) + 2))
        }
      }
      return 48
    }
  },
  metaInfo () {
    return {
      // about page browser title
      title: 'Program',
      meta: [
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: `${'Program'} | PhotoVogue Festival 2020` }
      ]
    }
  }
}
</script>
<style lang="scss" scoped>

.calendar-page {
  width: 100vw;
  overflow-x: hidden;
}

.program-holder {
  position: relative;
  width: 130%;
  left: -15%;
  @include breakpoint(sm-and-up) {
    width: 100%;
    left: 0;
  }
}

h2.subtitle {
  opacity: 0.6;
}

::v-deep .date-item {
  span {
    justify-content: flex-start;

    @include breakpoint(md-and-up) {
      justify-content: center;
    }
  }
}

::v-deep .v-event-timed {
  height: auto !important;
}

::v-deep .v-calendar-daily__scroll-area,
::v-deep .v-calendar-daily__body {
  overflow: visible !important;
}

::v-deep .theme--light.v-calendar-daily {
  border: 0 !important;
  background-color: transparent;
  overflow: visible;

  .v-calendar-daily__day {
    border-right: 0 !important;
    border-bottom: 0 !important;
    border-left: 0;
    margin-left: 85px;
  }
}

::v-deep .v-calendar-daily__pane {
  overflow: visible;
}

::v-deep .v-calendar .v-event-timed {
  overflow: visible;
}

// ::v-deep .theme--light.v-calendar-events .v-event-timed,
::v-deep .theme--light.v-calendar-daily,
::v-deep .theme--light.v-calendar-daily .v-calendar-daily__day-interval,
::v-deep .theme--light.v-calendar-daily .v-calendar-daily__interval::after { // stylelint-disable-line
  border: 0 !important;
}

::v-deep .theme--light.v-calendar-daily .v-calendar-daily__intervals-body {
  border-right: #000 1px solid;
}

// ::v-deep .theme--light.v-calendar-events .v-event-timed {
//   transform: translateX(50px);
// }

::v-deep .theme--light.v-calendar-daily .v-calendar-daily__interval { // stylelint-disable-line
  position: relative;
  padding-right: 0;
  text-align: left;

  &:nth-child(1) {
    // display: none;
  }

  .v-calendar-daily__interval-text { // stylelint-disable-line
    position: relative;
    left: 65px;
    padding-bottom: 5px;
    font-size: 0.85rem;
    padding-right: 0;

    @include breakpoint(md-and-up) {
      font-size: 0.7rem;
    }
  }

  &::after {
    content: url('../assets/img/calendar-interval-divider.inline.svg');
    position: relative;
    top: -20px;
    left: 20px;
  }
}

::v-deep .v-event-timed {
  pointer-events: none !important;
}

.event-btn {
  pointer-events: all;
}

.program-event {
  // border: 1px solid #000;
  // background-color: rgba(map-get($shades, 'black'), 0.06);
  white-space: normal;
}

.highlighted-item {
  position: relative;

  &::after {
    content: '';
    background-image: url('../assets/img/circle-highlight.inline.svg');
    background-position: 50% 50%;
    background-size: cover;
    width: calc(100% + 40px);
    padding-top: 46%;
    position: absolute;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    opacity: 0;
    transition: opacity 0.25s;
  }

  &--top {
    &::after {
      top: 60px;
      left: calc(50% - 20px);
      width: 140%;
    }
  }

  &--center {
    &::after {
      top: 50%;
    }
  }

  &--red {
    &::after {
      background-image: url('../assets/img/circle-highlight-red.inline.svg');
    }
  }

  .v-btn--active &,
  &:hover {
    &::after {
      opacity: 1;
    }
  }
}

.live-collage {
  position: relative;
  background-image: url('../assets/img/live-popup-bg.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;
  max-width: 1300px;

  @include breakpoint('sm-and-down') {
    background-size: auto 100%;
    background-position: 50% 50%;
  }
}

.collage-content {
  position: absolute;
  top: 50%;
  left: 50%;
  // overflow-x: hidden;
  // overflow-y: auto;
  // -webkit-overflow-scrolling: touch;
  // collage: 1300x910
  // content: 844x419
  width: #{(844/1300)*100%};
  height: auto;
  // height: #{(419/910)*100%};
  transform: translate3d(-50%, -50%, 0) rotate(-7deg);

  @include breakpoint('sm-and-down') {
    position: relative;
    width: 100%;
    top: 0;
    left: 0;
    transform: translate3d(0, 0, 0);
    margin: 0 auto;
  }
}

.live-link,
.watch-the-talks {
  font-size: 1.5rem !important;
  position: absolute;
  right: 0;
  bottom: 0;
  transform: translate3d(0, 200%, 0) rotate(7deg);

  @include breakpoint('sm-and-down') {
    font-size: 0.8rem !important;
    transform: translate3d(-10%, 150%, 0) rotate(7deg);
  }

  &.disabled {
    opacity: 0.25;
    pointer-events: none;
  }

  .arrow {
    position: absolute;
    top: 0;
    left: 0;
    fill: currentColor;
    transform: translate3d(-150%, -50%, 0) rotate(90deg);
  }
}

.watch-the-talks {
  transform: translate3d(-100%, 100%, 0) rotate(7deg);

  @include breakpoint('sm-and-down') {
    transform: translate3d(-80%, 100%, 0) rotate(7deg);
  }

  .arrow {
    position: absolute;
    top: 0;
    left: 0;
    fill: currentColor;
    transform: translate3d(150%, 100%, 0) rotate(-90deg);

    @include breakpoint('sm-and-down') {
      transform: translate3d(100%, 50%, 0) rotate(-90deg);
    }
  }
}

.deleted-text {
  position: absolute;

  &--3 {
    right: -300px;
    top: 0;
    transform: rotate(20deg);
  }

  &--1 {
    left: -100px;
    top: 380px;
    transform: rotate(20deg);
  }

  &--2 {
    left: -100px;
    top: 480px;
    transform: rotate(10deg);
  }
}

// .event-line-now-btn {
//   position: absolute;
//   top: 20px;
//   left: -100px;
//   transform: translateX(-100%);
// }

</style>
